<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import axios from 'axios';

const props = defineProps({
    kecamatans: Array // Passed from controller
});

const artikels = ref([]);
const loading = ref(true);
const saving = ref(false);
const isEditing = ref(false);
const processingStatusId = ref(null); // Keep for compatibility if needed, or remove if unused.
const processingPublishId = ref(null);
const processingFeaturedId = ref(null);

// ... existing code ...

const togglePublish = async (artikel) => {
    if (processingPublishId.value) return;
    processingPublishId.value = artikel.id;
    try {
        const response = await axios.post('/admin/artikel-kecamatan/toggle-publish', { 
            id: artikel.id,
            is_published: !artikel.is_published 
        });
        if (response.data.status === 'success') {
            artikel.is_published = response.data.is_published;
        } else {
             showModal('warning', [response.data.message]);
        }
    } catch (error) {
        showModal('error', ['Gagal update status publish']);
    } finally {
        processingPublishId.value = null;
    }
};

const toggleFeatured = async (artikel) => {
    if (processingFeaturedId.value) return;
    processingFeaturedId.value = artikel.id;
    try {
        const response = await axios.post('/admin/artikel-kecamatan/toggle-featured', { 
            id: artikel.id,
            is_featured: !artikel.is_featured 
        });
        if (response.data.status === 'success') {
            artikel.is_featured = response.data.is_featured;
        } else {
            showModal('warning', [response.data.message]);
        }
    } catch (error) {
        showModal('error', ['Gagal update status unggulan']);
    } finally {
        processingFeaturedId.value = null;
    }
};
const form = ref({
    id: null,
    kecamatan_id: '',
    judul: '',
    slug: '',
    konten: '',
    featured_image: null,
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    is_published: true,
    is_featured: false
});

const formErrors = ref({}); // Client-side validation errors

// Modals
const modalType = ref('error');
const errorModalContent = ref([]);
const showModal = (type, messages) => {
    modalType.value = type;
    errorModalContent.value = Array.isArray(messages) ? messages : [messages];
    const modal = new bootstrap.Modal(document.getElementById('errorModal'));
    modal.show();
};

const deleteId = ref(null);
const deleteName = ref('');
const deleteConfirmedCheck = ref(false);

const originalData = ref(null);

// CKEditor
let kontenEditor = null;

const initEditors = () => {
    if (typeof CKEDITOR === 'undefined') return;
    
    if (CKEDITOR.instances['konten']) {
        try { CKEDITOR.instances['konten'].destroy(); } catch (e) {}
    }

    kontenEditor = CKEDITOR.replace('konten', {
        height: 300,
        toolbar: [
            { name: 'styles', items: ['Format'] },
            { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline'] },
            { name: 'paragraph', items: ['NumberedList', 'BulletedList'] },
            { name: 'links', items: ['Link', 'Unlink'] }
        ],
        format_tags: 'p;h1;h2;h3;h4;h5;h6'
    });

    kontenEditor.on('change', () => {
        form.value.konten = kontenEditor.getData();
    });
};

onBeforeUnmount(() => {
    if (typeof CKEDITOR !== 'undefined' && CKEDITOR.instances['konten']) {
        try { CKEDITOR.instances['konten'].destroy(); } catch (e) {}
    }
});

const slugify = (text) => {
  return text.toString().toLowerCase().trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

watch(() => form.value.judul, (newVal) => {
    if (!isEditing.value && newVal) {
        form.value.slug = slugify(newVal);
    }
});

const loadArtikels = async () => {
    loading.value = true;
    try {
        const response = await axios.get('/admin/artikel-kecamatan/get');
        if (response.data.status === 'success') {
            artikels.value = response.data.data.artikel;
        }
    } catch (error) {
        console.error('Error:', error);
    } finally {
        loading.value = false;
    }
};

const handleFileChange = (e) => {
    form.value.featured_image = e.target.files[0];
};

const resetForm = () => {
    isEditing.value = false;
    originalData.value = null; // Reset comparison data
    form.value = {
        id: null,
        kecamatan_id: '',
        judul: '',
        slug: '',
        konten: '',
        featured_image: null,
        meta_title: '',
        meta_description: '',
        meta_keywords: '',
        is_published: true,
        is_featured: false
    };
    if (kontenEditor) kontenEditor.setData('');
    const fileInput = document.getElementById('featured_image');
    if (fileInput) fileInput.value = '';
    isEditing.value = false;
    formErrors.value = {};
};

const edit = (item) => {
    form.value = {
        id: item.id,
        kecamatan_id: item.kecamatan?.id || item.kecamatan_id,
        judul: item.judul,
        slug: item.slug || '',
        konten: item.konten || '',
        featured_image: null, // Don't preload file input
        meta_title: item.meta_title || '',
        meta_description: item.meta_description || '',
        meta_keywords: item.meta_keywords || '',
        is_published: !!item.is_published,
        is_featured: !!item.is_featured
    };
    
    // Store original data for comparison
    originalData.value = {
        kecamatan_id: item.kecamatan?.id || item.kecamatan_id,
        judul: item.judul,
        slug: item.slug || '',
        konten: item.konten || '', // Will be updated by callback
        featured_image: null,
        meta_title: item.meta_title || '',
        meta_description: item.meta_description || '',
        meta_keywords: item.meta_keywords || '',
        is_published: !!item.is_published,
        is_featured: !!item.is_featured
    };

    if (kontenEditor) {
        kontenEditor.setData(item.konten || '', {
            callback: function() {
                if (originalData.value) originalData.value.konten = this.getData();
            }
        });
    }
    isEditing.value = true;
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const submitForm = async () => {
    saving.value = true;
    await new Promise(resolve => setTimeout(resolve, 300));

    // Sync CKEditor
    if (kontenEditor) form.value.konten = kontenEditor.getData();

    // Client-side Validation
    formErrors.value = {};
    const errors = [];
    if (!form.value.kecamatan_id) errors.push('Kecamatan wajib dipilih');
    if (!form.value.judul || form.value.judul.trim() === '') errors.push('Judul wajib diisi');
    if (!form.value.konten || form.value.konten.trim() === '') errors.push('Konten artikel wajib diisi');
    
    if (errors.length > 0) {
        saving.value = false;
        showModal('error', errors);
        return;
    }

    const formData = new FormData();
    
    // Check for changes if editing
    if (isEditing.value) {
        const currentData = {
            kecamatan_id: form.value.kecamatan_id,
            judul: form.value.judul,
            slug: form.value.slug,
            konten: form.value.konten,
            featured_image: form.value.featured_image,
            meta_title: form.value.meta_title,
            meta_description: form.value.meta_description,
            meta_keywords: form.value.meta_keywords,
            is_published: form.value.is_published,
            is_featured: form.value.is_featured
        };
        
        // Note: Comparison with image logic might need adjustment if image is nullable/object
        // But since featured_image is null in originalData and null in form unless changed...
        // If user uploads a file, form.featured_image is a File object, originalData is null. != match.
        // If user doesn't upload, both null. == match.
        // Correct.
        
        if (originalData.value && JSON.stringify(currentData) === JSON.stringify(originalData.value)) {
            saving.value = false;
            showModal('warning', ['Tidak ada perubahan data yang disimpan.']);
            return;
        }
    }

    formData.append('kecamatan_id', form.value.kecamatan_id);
    formData.append('judul', form.value.judul);
    formData.append('slug', form.value.slug);
    formData.append('konten', form.value.konten);
    formData.append('meta_title', form.value.meta_title);
    formData.append('meta_description', form.value.meta_description);
    formData.append('meta_keywords', form.value.meta_keywords);
    formData.append('is_published', form.value.is_published ? 1 : 0);
    formData.append('is_featured', form.value.is_featured ? 1 : 0);
    
    if (form.value.featured_image) {
        formData.append('featured_image', form.value.featured_image);
    }
    
    let url = '/admin/artikel-kecamatan';
    if (isEditing.value) {
        formData.append('_method', 'PUT');
        url = `/admin/artikel-kecamatan/${form.value.id}`;
    }
    
    try {
        const response = await axios.post(url, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (response.data.status === 'success') {
            resetForm();
            loadArtikels();
        } else if (response.data.status === 'info') {
            showModal('warning', [response.data.message]);
        }
    } catch (error) {
        if (error.response?.data?.errors) {
            // Validation errors
            const msgs = Object.values(error.response.data.errors).flat();
            showModal('error', msgs);
        } else {
            showModal('error', [error.response?.data?.message || 'Terjadi kesalahan']);
        }
    } finally {
        saving.value = false;
    }
};

const confirmDelete = (item) => {
    deleteId.value = item.id;
    deleteName.value = item.judul;
    deleteConfirmedCheck.value = false;
    const modal = new bootstrap.Modal(document.getElementById('deleteModal'));
    modal.show();
};

const submitDelete = async () => {
    if (!deleteId.value) return;
    try {
        await axios.delete(`/admin/artikel-kecamatan/${deleteId.value}`);
        // Hide modal
        const modalEl = document.getElementById('deleteModal');
        const modal = bootstrap.Modal.getInstance(modalEl);
        if (modal) modal.hide();
        loadArtikels();
    } catch (error) {
        showModal('error', ['Gagal menghapus data']);
        // Hide modal even on error
         const modalEl = document.getElementById('deleteModal');
        const modal = bootstrap.Modal.getInstance(modalEl);
        if (modal) modal.hide();
    }
};



onMounted(() => {
    loadArtikels();
    nextTick(() => initEditors());
});
</script>

<template>
    <Head title="Artikel Kecamatan" />

    <div class="container-full">
        <div class="content-header">
            <div class="d-flex align-items-center justify-content-between">
                <div class="d-block">
                    <h4 class="page-title">Kelola Artikel Kecamatan</h4>
                    <div class="d-inline-block align-items-center">
                        <nav>
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item"><Link href="/admin/dashboard"><i class="mdi mdi-home-outline"></i></Link></li>
                                <li class="breadcrumb-item active" aria-current="page">Artikel Kecamatan</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </div>
        </div>

        <section class="content">
            <div class="row">
                <!-- Left Column: Form -->
                <div class="col-lg-5 col-12 mb-4">
                    <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title text-primary">
                                <i class="ti-plus me-2" v-if="!isEditing"></i>
                                <i class="ti-pencil me-2" v-else></i>
                                {{ isEditing ? 'Edit Artikel' : 'Tambah Artikel' }}
                            </h4>
                        </div>
                        <form @submit.prevent="submitForm">
                            <div class="box-body">
                                <div class="form-group mb-3">
                                    <label for="kecamatan_id" class="form-label">Kecamatan <span class="text-danger">*</span></label>
                                    <select id="kecamatan_id" name="kecamatan_id" v-model="form.kecamatan_id" class="form-control" :disabled="loading">
                                        <option value="">Pilih Kecamatan</option>
                                        <option v-for="k in props.kecamatans" :key="k.id" :value="k.id">{{ k.nama_kecamatan }}</option>
                                    </select>
                                </div>
                                <div class="form-group mb-3">
                                    <label for="judul" class="form-label">Judul <span class="text-danger">*</span></label>
                                    <input type="text" id="judul" name="judul" v-model="form.judul" class="form-control" placeholder="Judul artikel">
                                </div>
                                <div class="form-group mb-3">
                                    <label for="slug" class="form-label">Slug</label>
                                    <input type="text" id="slug" name="slug" v-model="form.slug" class="form-control" placeholder="slug-artikel">
                                </div>
                                <div class="form-group mb-3">
                                    <label for="konten" class="form-label">Konten</label>
                                    <textarea id="konten" name="konten" class="form-control"></textarea>
                                </div>
                                <div class="form-group mb-3">
                                    <label for="featured_image" class="form-label">Gambar Utama</label>
                                    <input type="file" id="featured_image" name="featured_image" @change="handleFileChange" class="form-control" accept="image/*">
                                    <small class="text-muted d-block mt-1">Kosongkan jika tidak ingin mengubah gambar (saat edit).</small>
                                </div>

                                <div class="card bg-light-light mb-0 mt-3">
                                    <div class="card-body py-3">
                                        <div class="row">
                                            <div class="col-6">
                                                <div class="form-check form-switch mb-2">
                                                    <input class="form-check-input" type="checkbox" v-model="form.is_published" id="switchPublish">
                                                    <label class="form-check-label" for="switchPublish">Publish</label>
                                                </div>
                                            </div>
                                            <div class="col-6">
                                                <div class="form-check form-switch">
                                                    <input class="form-check-input" type="checkbox" v-model="form.is_featured" id="switchFeatured">
                                                    <label class="form-check-label" for="switchFeatured">Unggulan</label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- SEO Meta Data -->
                                <div class="card bg-light-light mb-0 mt-3">
                                    <div class="card-body py-3">
                                        <h5 class="text-primary mb-3"><i class="ti-world me-2"></i>SEO Meta Data</h5>
                                        <div class="form-group mb-3">
                                            <label for="meta_title" class="form-label">Meta Title</label>
                                            <input type="text" id="meta_title" name="meta_title" v-model="form.meta_title" class="form-control" placeholder="Judul pencarian Google">
                                        </div>
                                        <div class="form-group mb-3">
                                            <label for="meta_description" class="form-label">Meta Description</label>
                                            <textarea id="meta_description" name="meta_description" v-model="form.meta_description" class="form-control" rows="3" placeholder="Deskripsi di hasil pencarian"></textarea>
                                        </div>
                                        <div class="form-group mb-0">
                                            <label for="meta_keywords" class="form-label">Meta Keywords</label>
                                            <input type="text" id="meta_keywords" name="meta_keywords" v-model="form.meta_keywords" class="form-control" placeholder="Kata kunci, pisahkan dengan koma">
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="box-footer text-end">
                                <button v-if="isEditing" type="button" class="btn btn-warning me-2" @click="resetForm" :disabled="saving">
                                    <i class="ti-trash me-2"></i> Batal
                                </button>
                                <button type="submit" class="btn btn-primary" :disabled="saving">
                                    <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                                    <i v-else class="ti-save-alt me-2"></i>
                                    {{ saving ? 'Menyimpan...' : (isEditing ? 'Simpan Perubahan' : 'Simpan') }}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                <!-- Right Column: Table -->
                <div class="col-lg-7 col-12">
                    <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title text-primary"><i class="ti-view-list me-2"></i>Daftar Artikel</h4>
                        </div>
                        <div class="box-body">
                            <div class="table-responsive">
                                <table class="table table-hover mb-0">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Gambar</th>
                                            <th>Judul / Kecamatan</th>
                                            <th class="text-nowrap" style="width: 1%;">Publish</th>
                                            <th class="text-nowrap" style="width: 1%;">Unggulan</th>
                                            <th class="text-center text-nowrap" style="width: 1%;">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="loading"><td colspan="6" class="text-center p-5"><div class="spinner-border text-primary"></div></td></tr>
                                        <tr v-else-if="artikels.length === 0"><td colspan="6" class="text-center py-4">Belum ada data artikel.</td></tr>
                                        <tr v-else v-for="(item, index) in artikels" :key="item.id" class="align-middle">
                                            <td>{{ index + 1 }}</td>
                                            <td>
                                                <img v-if="item.featured_image" :src="'/storage/' + item.featured_image" class="rounded" style="width: 60px; height: 40px; object-fit: cover;">
                                                <span v-else class="text-muted small">No Image</span>
                                            </td>
                                            <td>
                                                <div class="fw-bold">{{ item.judul }}</div>
                                                <div class="small text-muted">{{ item.kecamatan?.nama_kecamatan || '-' }}</div>
                                            </td>
                                            <td class="text-nowrap text-center">
                                                <button type="button" class="btn btn-sm rounded-1 px-3" :class="item.is_published ? 'btn-success' : 'btn-danger'" @click="togglePublish(item)" :disabled="processingPublishId === item.id" title="Klik untuk mengubah status">
                                                    <span v-if="processingPublishId === item.id" class="spinner-border spinner-border-sm me-1"></span>
                                                    <i v-else class="fa me-1" :class="item.is_published ? 'fa-check-circle' : 'fa-times-circle'"></i>
                                                    {{ item.is_published ? 'Published' : 'Draft' }}
                                                </button>
                                            </td>
                                            <td class="text-nowrap text-center">
                                                <button type="button" class="btn btn-sm rounded-1 px-3" :class="item.is_featured ? 'btn-warning' : 'btn-secondary'" @click="toggleFeatured(item)" :disabled="processingFeaturedId === item.id" title="Klik untuk mengubah status">
                                                    <span v-if="processingFeaturedId === item.id" class="spinner-border spinner-border-sm me-1"></span>
                                                    <i v-else class="fa me-1" :class="item.is_featured ? 'fa-star' : 'fa-star-o'"></i>
                                                    {{ item.is_featured ? 'Unggulan' : 'Biasa' }}
                                                </button>
                                            </td>
                                            <td class="text-center text-nowrap">
                                                <button class="btn btn-primary btn-sm me-1" @click="edit(item)" title="Edit"><i class="fa fa-edit"></i></button>
                                                <button class="btn btn-danger btn-sm" @click="confirmDelete(item)" title="Hapus"><i class="fa fa-trash"></i></button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Delete Modal -->
        <div class="modal fade" id="deleteModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header bg-danger text-white">
                        <h5 class="modal-title"><i class="ti-alert me-2"></i>Konfirmasi Hapus</h5>
                        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p>Apakah Anda yakin ingin menghapus artikel <strong>{{ deleteName }}</strong>?</p>
                        <p class="text-danger small mb-3">Tindakan ini tidak dapat dibatalkan.</p>
                        
                        <div class="form-check list-group-item list-group-item-danger p-3 rounded mb-0">
                            <input class="form-check-input" type="checkbox" id="confirmCheck" v-model="deleteConfirmedCheck">
                            <label class="form-check-label text-dark" for="confirmCheck">
                                Saya mengerti dan yakin ingin menghapus data ini.
                            </label>
                        </div>
                    </div>
                    <div class="modal-footer text-end">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"><i class="fa fa-times me-2"></i>Batal</button>
                        <button type="button" class="btn btn-danger" :disabled="!deleteConfirmedCheck" @click="submitDelete">
                            <i class="fa fa-trash me-2"></i>Ya, Hapus
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Error/Warning Modal -->
        <div class="modal fade" id="errorModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header text-white" :class="modalType === 'warning' ? 'bg-warning text-dark' : 'bg-danger'">
                        <h5 class="modal-title">
                            <i class="me-2" :class="modalType === 'warning' ? 'fa fa-exclamation-triangle' : 'ti-alert'"></i>
                            {{ modalType === 'warning' ? 'Peringatan' : 'Peringatan Validasi' }}
                        </h5>
                        <button type="button" class="btn-close" :class="modalType === 'warning' ? '' : 'btn-close-white'" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p class="mb-3" v-if="modalType === 'error'">Mohon perbaiki kesalahan berikut sebelum menyimpan:</p>
                        <ul class="list-group list-group-flush">
                            <li class="list-group-item" :class="modalType === 'warning' ? 'list-group-item-warning' : 'list-group-item-danger'" v-for="(error, index) in errorModalContent" :key="index">
                                <i class="fa me-2" :class="modalType === 'warning' ? 'fa-info-circle' : 'fa-exclamation-circle'"></i>{{ error }}
                            </li>
                        </ul>
                    </div>
                    <div class="modal-footer text-end">
                        <button type="button" class="btn" :class="modalType === 'warning' ? 'btn-warning' : 'btn-danger'" data-bs-dismiss="modal"><i class="fa fa-check-square-o me-2"></i>Mengerti</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
